import { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Button,
	Box,
	Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { validateEmail } from "../../utils/validation";
import { useChangeEmailMutation } from "../../services/queryHooks";
import { Loading } from "../../components/common/Loading";
import { Check } from "@mui/icons-material";
export default function ChangeEmailModal({ isOpen, onClose }) {
	const { t, i18n } = useTranslation();
	const {
		mutate: changeEmail,
		isPending,
		isSuccess,
		data,
		reset,
		error,
	} = useChangeEmailMutation();

	const [form, setForm] = useState({
		email: "",
		confirmEmail: "",
	});

	const [errors, setErrors] = useState({
		email: "",
		confirmEmail: "",
	});

	useEffect(() => {
		if (isOpen) {
			setForm({
				email: "",
				confirmEmail: "",
			});
			setErrors({
				email: "",
				confirmEmail: "",
			});
		}
	}, [isOpen]);

	useEffect(() => {
		if (error) {
			setErrors({
				email: error?.message,
				confirmEmail: error?.message,
			});
		}
	}, [error, t]);

	useEffect(() => {
		if (!isSuccess) return;

		const timer = setTimeout(() => {
			onClose();
			reset();
		}, 3000);

		return () => clearTimeout(timer);
	}, [isSuccess, onClose, reset]);

	const handleChange = (field) => (e) => {
		setForm((prev) => ({
			...prev,
			[field]: e.target.value,
		}));
	};

	const validateInputs = () => {
		let isValid = true;
		const newErrors = {
			email: "",
			confirmEmail: "",
		};

		const emailError = validateEmail(form.email, t);
		if (emailError) {
			newErrors.email = emailError;
			isValid = false;
		}

		const confirmEmailError = validateEmail(form.confirmEmail, t);
		if (confirmEmailError) {
			newErrors.confirmEmail = confirmEmailError;
			isValid = false;
		}

		if (
			!emailError &&
			!confirmEmailError &&
			form.email !== form.confirmEmail
		) {
			newErrors.confirmEmail = t("emailsDoNotMatch");
			isValid = false;
		}

		setErrors(newErrors);
		return isValid;
	};

	const submitForm = (event) => {
		event.preventDefault();
		if (!validateInputs()) return;

		changeEmail({
			newEmail: form.email,
			lang: i18n.language,
		});
	};

	return (
		<>
			<Dialog open={isOpen} onClose={onClose} maxWidth="xs" fullWidth>
				{isPending && <Loading />}
				{isSuccess && data?.message && (
					<Box
						sx={{
							position: "fixed",
							top: "100px",
							left: "50%",
							transform: "translateX(-50%)",
							zIndex: 5,
							width: "auto",
						}}
					>
						<Alert
							icon={<Check fontSize="inherit" />}
							severity="success"
						>
							{data.message}
						</Alert>
					</Box>
				)}
				<DialogTitle>{t("changeEmail")}</DialogTitle>

				<form onSubmit={submitForm} noValidate>
					<DialogContent
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 2,
						}}
					>
						<TextField
							label={t("email")}
							value={form.email}
							onChange={handleChange("email")}
							error={!!errors.email}
							helperText={errors.email}
							autoComplete="email"
						/>

						<TextField
							label={t("confirmEmail")}
							value={form.confirmEmail}
							onChange={handleChange("confirmEmail")}
							error={!!errors.confirmEmail}
							helperText={errors.confirmEmail}
							autoComplete="email"
						/>
					</DialogContent>

					<DialogActions>
						<Button onClick={onClose}>{t("cancel")}</Button>
						<Button
							type="submit"
							variant="contained"
							disabled={isPending || isSuccess}
						>
							{t("save")}
						</Button>
					</DialogActions>
				</form>
			</Dialog>
		</>
	);
}
